import {Component, EventEmitter, OnInit, Output, ViewChild} from '@angular/core';
import {MatTableDataSource} from '@angular/material/table';
import {MatSort} from '@angular/material/sort';
import {MatPaginator} from '@angular/material/paginator';
import * as moment from 'moment';
import {HttpClient} from '@angular/common/http';
import {Router} from '@angular/router';
import {AbstractBffApi, AosOrderInterface} from '../../../../models/sharedinterface';
import {ApiFactoryService} from '../../../../shared/services/api-factory.service';
import {GlobalsService} from '../../../../shared/services/globals.service';
import {ShareddataService} from '../../../../shared/services/shareddata.service';
import {I18nService} from '../../../../shared/services/i18n.service';
import {AuthenticationService} from '../../../../security/authentication.service';

@Component({
  selector: 'app-aos-order-search',
  templateUrl: './aos-order-search.component.html',
  styleUrls: ['./aos-order-search.component.scss']
})
export class AosOrderSearchComponent implements OnInit {

  @Output() onClose: EventEmitter<any> = new EventEmitter();

  private bffapi: AbstractBffApi;

  // Order types
  public orderTypes: { name: string, label: string }[];

  // Users
  public users: { value: number, label: string }[];

  // Countries
  public markets: { id: number, country_label: string }[];

  // Companies
  public companies: { id: number, company_label: string }[];

  // Product types
  public productTypes: { value: number, label: string }[];

  // max date
  public today: Date = new Date();

  // search options
  public searchOptions = {
    maxDateInterval: 24, // month
    defaultDateInterval: 3 // month
  };

  public searchObject: {
    orderType: 'aos' | 'dos' | 'webshop' | 'instore';
    productTypeId: number;
    fromDate: Date;
    untilDate: Date;
    marketId: number;
    companyId: number;
    userId: number;
    textSearch: string;
  } = {
    // orderType: null,
    orderType: 'aos',
    productTypeId: 0,
    fromDate: moment().subtract(this.searchOptions.defaultDateInterval, 'months').toDate(), // default range = 3 months
    untilDate: new Date(),
    marketId: 0,
    companyId: 0,
    userId: 0,
    textSearch: ''
  };

  displayedColumns: any = [];
  keyColumns: any = []; // Columns with link
  datalistsource: MatTableDataSource<any>; // MatTableDataSource

  public currentOrder: AosOrderInterface;

  private serverRoot: string;

  public isSearching = false;

  @ViewChild(MatSort, {static: true}) sort: MatSort;
  @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;

  // export options
  public exportOptions = {
    fileName: this.i18n.get('orders_aos_orders_export_filename', 'orders'),
    sheet: this.i18n.get('orders_export_sheetname', 'orders'),
  };

  public dateRangeInvalid = false;

  constructor(
    private apifactory: ApiFactoryService,
    protected http: HttpClient,
    private globalsservice: GlobalsService,
    private router: Router,
    private shareData: ShareddataService,
    public i18n: I18nService,
    private authservice: AuthenticationService
  ) {
    // Sets the type of orders to fetch
    this.bffapi = this.apifactory.createBffApi();
    this.serverRoot = globalsservice.getBffapiroot();

    this.users = [];
    this.markets = [];
    this.companies = [];
    this.productTypes = [];
    this.orderTypes = [
      {name: 'aos', label: 'AOS'},
      {name: 'webshop', label: 'WebShop'}
      // {name: 'instore', label: 'InStore'}
    ];

    this.currentOrder = null;
  }

  ngOnInit(): void {
    this.displayedColumns = ['nr', 'created', 'user_company', 'country_label', 'productTypeLabel', 'assetName', 'd_width', 'd_height', 'd_unit', 'status', 'actions'];
    // Loads search criteria, should be done automatically when user picks up an order type
    this.loadSearchCriteria();
  }

  getColumnLabel(fieldName: string) {
    switch (fieldName) {
      case 'user_company':
        return this.i18n.get('global_company_user_label');

      case 'country_label':
        return this.i18n.get('global_market_label');

      case 'nr':
        return this.i18n.get('orders_order_nr_label', 'orders');

      case 'created':
        return this.i18n.get('orders_order_date_label', 'orders');

      case 'productTypeLabel':
        return this.i18n.get('orders_order_product_label', 'orders');

      case 'd_width':
        return this.i18n.get('orders_order_width_label', 'orders');

      case 'd_height':
        return this.i18n.get('orders_order_height_label', 'orders');

      case 'd_unit':
        return this.i18n.get('orders_order_unit_label', 'orders');

      case 'assetName':
        return this.i18n.get('orders_order_asset_name_label', 'orders');

      case 'actions':
        return '';

      case 'status':
        return '';

      default:
        return fieldName;
    }
  }

  getFormattedValue(fieldName: string, value: any) {
    if (fieldName === 'created') {
      return moment(value.date).format('DD/MM/YYYY HH:mm');
    }
    return value;
  }

  onOrderTypeChange() {
    this.datalistsource = null;
    this.loadSearchCriteria();
  }

  loadSearchCriteria() {
    // Loads AOS search criterias
    if (this.searchObject.orderType === 'aos') {
      const productTypesQuery = this.bffapi.getAOSOrderProductTypes();
      productTypesQuery.subscribe((data) => {
        this.productTypes = data.results;
      }, error => {
        if (error.status === 403) { // Session expired
          this.authservice.setLoggedin(false);
          this.router.navigate(['']);
        }
      });
      const usersQuery = this.bffapi.getAOSOrderUsers();
      usersQuery.subscribe((data) => {
        this.users = data.results;
      }, error => {
        if (error.status === 403) { // Session expired
          this.authservice.setLoggedin(false);
          this.router.navigate(['']);
        }
      });
      const countriesQuery = this.bffapi.getAOSOrderCountries();
      countriesQuery.subscribe((data) => {
        this.markets = data.results;
      }, error => {
        if (error.status === 403) { // Session expired
          this.authservice.setLoggedin(false);
          this.router.navigate(['']);
        }
      });
      const companiesQuery = this.bffapi.getAOSOrderCompanies();
      companiesQuery.subscribe((data) => {
        this.companies = data.results;
      }, error => {
        if (error.status === 403) { // Session expired
          this.authservice.setLoggedin(false);
          this.router.navigate(['']);
        }
      });
    }
  }

  search() {

    if (!this.validateSearch()) {
      return;
    }

    this.isSearching = true;

    const query = this.bffapi.searchOrders(this.searchObject);
    query.subscribe((data) => {
        this.isSearching = false;
        this.datalistsource = new MatTableDataSource(data.results);
        this.datalistsource.sort = this.sort;
        this.datalistsource.paginator = this.paginator;
        this.onDataSourceReady();
      },
      error => {
        if (error.status === 403) { // Session expired
          this.authservice.setLoggedin(false);
          this.router.navigate(['']);
        }
        this.isSearching = false;
      });
  }

  validateSearch() {
    let isValid = false;

    // Validates date range
    const start = moment(this.searchObject.fromDate);
    const end = moment(this.searchObject.untilDate);
    const monthsInterval = end.diff(start, 'months');
    if (monthsInterval <= this.searchOptions.maxDateInterval) {
      this.dateRangeInvalid = false;
      isValid = true;
    } else {
      this.dateRangeInvalid = true;
    }

    // Validates

    return isValid;
  }

  showDetails(order: AosOrderInterface) {
    this.currentOrder = order;
  }

  closeDetails() {
    this.currentOrder = null;
  }

  closeSearch() {
    this.onClose.emit();
  }

  renewOrder(order: AosOrderInterface) {
    // Redirect to the only aos prod tool of the mandant with autologin info

    const renewPath = this.globalsservice.getAosProdRenewPath(
      this.shareData.getCurrentlanguage(),
      this.shareData.getUserTools(), order);

    this.router.navigate(renewPath);
  } // renewOrder

  private onDataSourceReady() {
    this.datalistsource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'created':
          return new Date(item.created.date);
        default:
          return item[property];
      }
    };
  }
}
